export const CANONICAL_SECTION_LOCALE_PRIORITY = ['en', 'de'];
export function publishedLocales(sections) {
    return Object.keys(sections ?? {}).filter(locale => (sections?.[locale]?.length ?? 0) > 0);
}
function preferredLocale(available) {
    if (available.length === 0)
        return undefined;
    return CANONICAL_SECTION_LOCALE_PRIORITY.find(locale => available.includes(locale)) ?? [...available].sort()[0];
}
export function canonicalSectionLocale(sections) {
    return preferredLocale(publishedLocales(sections));
}
export function resolveHiddenSections(sections, hidden) {
    const unchanged = {
        hidden: hidden ?? [],
        hiddenIndexes: [],
        unresolved: [],
        changed: false,
    };
    if (!hidden || hidden.length === 0)
        return unchanged;
    const locale = canonicalSectionLocale(sections);
    if (!locale)
        return unchanged;
    const titles = (sections[locale] ?? []).map(section => section.title);
    const resolved = [];
    const unresolved = [];
    for (const entry of hidden) {
        if (titles[entry.index] === entry.canonicalTitle) {
            resolved.push(entry);
            continue;
        }
        const movedTo = titles.indexOf(entry.canonicalTitle);
        if (movedTo >= 0)
            resolved.push({ index: movedTo, canonicalTitle: entry.canonicalTitle });
        else
            unresolved.push(entry);
    }
    const changed = unresolved.length > 0 ||
        resolved.length !== hidden.length ||
        resolved.some((entry, i) => entry.index !== hidden[i].index);
    return {
        hidden: resolved,
        hiddenIndexes: resolved.map(entry => entry.index).sort((a, b) => a - b),
        unresolved,
        changed,
    };
}
export function visibleSections(sections, hiddenIndexes) {
    const hidden = new Set(hiddenIndexes);
    return Object.fromEntries(Object.entries(sections).map(([locale, blocks]) => [locale, blocks.filter((_block, index) => !hidden.has(index))]));
}
export function sectionTitlesAt(sections, index) {
    return Object.fromEntries(Object.entries(sections)
        .filter(([, blocks]) => blocks[index] !== undefined)
        .map(([locale, blocks]) => [locale, blocks[index].title]));
}
export function resolveContentLocale(available, requested) {
    const locales = available.filter(locale => typeof locale === 'string' && locale.length > 0);
    const wanted = requested?.trim().toLowerCase();
    if (wanted && locales.includes(wanted))
        return wanted;
    return preferredLocale(locales);
}
const RISK_CLASS_PATTERNS = [
    /risk\s*class\s*(\d)\s*(?:out\s*of|of)\s*7/i,
    /risikoklasse\s*(\d)\s*von\s*7/i,
    /clase\s*de\s*riesgo\s*(\d)\s*(?:de|sobre)\s*7/i,
    /classe\s*de\s*risco\s*(\d)\s*(?:de|em)\s*7/i,
    /(?:gesamt)?risikoindikator[^.]{0,40}?(\d)\s*von\s*7/i,
    /summary\s*risk\s*indicator[^.]{0,40}?(\d)\s*(?:out\s*of|of)\s*7/i,
];
export function parseRiskClass(text) {
    for (const pattern of RISK_CLASS_PATTERNS) {
        const match = pattern.exec(text);
        if (match) {
            const value = Number(match[1]);
            if (value >= 1 && value <= 7)
                return value;
        }
    }
    return undefined;
}
const FILE_NAME_LOCALE_PREFIXES = {
    de: 'de',
    deu: 'de',
    en: 'en',
    eng: 'en',
    es: 'es',
    esp: 'es',
    pt: 'pt',
    por: 'pt',
    fr: 'fr',
    it: 'it',
};
export function localeFromFileName(fileName) {
    const match = /^([A-Za-z]{2,3})\s*-\s*/.exec(fileName.trim());
    return match ? FILE_NAME_LOCALE_PREFIXES[match[1].toLowerCase()] : undefined;
}
const ASCII_FOLDS = {
    ß: 'ss',
    ẞ: 'SS',
    ø: 'o',
    Ø: 'O',
    æ: 'ae',
    Æ: 'Ae',
    œ: 'oe',
    Œ: 'Oe',
    ł: 'l',
    Ł: 'L',
    þ: 'th',
    Þ: 'Th',
    ð: 'd',
    Ð: 'D',
    đ: 'd',
    Đ: 'D',
    ı: 'i',
    ŋ: 'n',
    Ŋ: 'N',
    ħ: 'h',
    Ħ: 'H',
    ŧ: 't',
    Ŧ: 'T',
};
const ASCII_FOLD_PATTERN = new RegExp(`[${Object.keys(ASCII_FOLDS).join('')}]`, 'g');
const COMBINING_MARKS = /[\u0300-\u036f\u1ab0-\u1aff\u1dc0-\u1dff\u20d0-\u20ff\ufe20-\ufe2f]/g;
export function foldToAscii(value) {
    return value
        .normalize('NFKD')
        .replace(COMBINING_MARKS, '')
        .replace(ASCII_FOLD_PATTERN, letter => ASCII_FOLDS[letter] ?? letter);
}
const HTML_ENTITIES = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: '\u00a0',
};
const fromCodePoint = (value, fallback) => Number.isInteger(value) && value >= 0 && value <= 0x10ffff ? String.fromCodePoint(value) : fallback;
export function decodeHtmlEntities(value) {
    return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, code) => {
        const lower = code.toLowerCase();
        if (lower.startsWith('#x'))
            return fromCodePoint(parseInt(lower.slice(2), 16), match);
        if (lower.startsWith('#'))
            return fromCodePoint(parseInt(lower.slice(1), 10), match);
        return Object.prototype.hasOwnProperty.call(HTML_ENTITIES, lower) ? HTML_ENTITIES[lower] : match;
    });
}
const APOSTROPHES = /['\u2018\u2019\u02bc\u02b9\u00b4\u2032`]/g;
const INVISIBLES = /[\u00ad\u115f\u1160\u180e\u200b-\u200f\u202a-\u202e\u2060-\u2064\u2066-\u2069\u2800\u3164\ufeff\uffa0]/g;
const INVISIBLE = new RegExp(INVISIBLES.source);
const DECORATIONS = /[\u2122\u00ae\u00a9\u2120]/g;
export function isBlankText(text) {
    return text.replace(INVISIBLES, '').replace(/\s+/g, '') === '';
}
export function isBlankHtml(html) {
    return isBlankText(decodeHtmlEntities(html.replace(/<[^>]+>/g, '')));
}
export function contentKey(label) {
    const strip = (value) => value.replace(APOSTROPHES, '').replace(INVISIBLES, '');
    const words = strip(foldToAscii(strip((label ?? '').replace(DECORATIONS, ''))))
        .split(/[^A-Za-z0-9]+/)
        .filter(Boolean);
    if (!words.some(word => /[A-Za-z]/.test(word)))
        return undefined;
    return words
        .map((word, index) => index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');
}
const HTML_TOKEN = /<(\/?)([a-z][a-z0-9]*)[^>]*>|([^<]+)/gi;
const BOLD_TAGS = new Set(['strong', 'b']);
const BLOCK_TAGS = new Set(['p', 'li', 'ul', 'ol']);
const COLONS = new Set([':', '\uff1a']);
const SOURCE_LINE_BREAKS = /[\r\n\u2028\u2029]/g;
const isFiller = (character) => /\s/.test(character) || INVISIBLE.test(character);
const isDigit = (character) => /^[0-9]$/.test(character ?? '');
export function splitLabelledItem(html) {
    const source = html ?? '';
    let text = '';
    const bold = [];
    const blockBreaks = new Set();
    let depth = 0;
    HTML_TOKEN.lastIndex = 0;
    for (let token = HTML_TOKEN.exec(source); token; token = HTML_TOKEN.exec(source)) {
        const [, closing, tag, words] = token;
        if (words !== undefined) {
            const decoded = decodeHtmlEntities(words).replace(SOURCE_LINE_BREAKS, ' ');
            text += decoded;
            for (let i = 0; i < decoded.length; i += 1)
                bold.push(depth > 0);
            continue;
        }
        const name = tag.toLowerCase();
        if (BOLD_TAGS.has(name)) {
            depth = Math.max(0, depth + (closing ? -1 : 1));
        }
        else if (name === 'br' || BLOCK_TAGS.has(name)) {
            if (name !== 'br')
                blockBreaks.add(text.length);
            text += '\n';
            bold.push(false);
        }
    }
    const wordsFrom = (from) => text
        .slice(from)
        .replace(INVISIBLES, '')
        .split('\n')
        .map(line => line.replace(/\s+/g, ' ').trim())
        .filter(Boolean)
        .join('\n');
    const whole = { body: wordsFrom(0) };
    let start = 0;
    while (start < text.length && isFiller(text[start]))
        start += 1;
    const insideNumber = (at) => isDigit(text[at - 1]) && isDigit(text[at + 1]) && !(bold[at] && !bold[at + 1]);
    let colon = start;
    while (colon < text.length && !(COLONS.has(text[colon]) && !insideNumber(colon)))
        colon += 1;
    if (colon >= text.length)
        return whole;
    let sawBold = false;
    for (let i = start; i < colon; i += 1) {
        if (blockBreaks.has(i))
            return whole;
        if (isFiller(text[i]))
            continue;
        if (!bold[i])
            return whole;
        sawBold = true;
    }
    if (!sawBold)
        return whole;
    const label = text.slice(start, colon).replace(INVISIBLES, '').replace(/\s+/g, ' ').trim();
    return label ? { label, body: wordsFrom(colon + 1) } : whole;
}
export function leadingLabel(html) {
    return splitLabelledItem(html).label;
}
function uniqueKeys(keys) {
    const counts = new Map();
    for (const key of keys)
        if (key)
            counts.set(key, (counts.get(key) ?? 0) + 1);
    return keys.map(key => (key && counts.get(key) === 1 ? key : undefined));
}
const shownItems = (list) => (list ?? []).flatMap((item, at) => (isBlankHtml(item) ? [] : [{ item, at }]));
function pairable(shownServed, shownCanonical) {
    return (shownServed.length === shownCanonical.length &&
        shownServed.every((entry, place) => entry.at === shownCanonical[place].at));
}
export function keySections(served, canonical, unseenIndexes = []) {
    const servedItems = served.map(section => shownItems(section.list));
    const withoutBlanks = (section, index) => section.list ? { ...section, list: servedItems[index].map(entry => entry.item) } : { ...section };
    if (!canonical || canonical.length !== served.length)
        return served.map(withoutBlanks);
    const canonicalItems = canonical === served ? servedItems : canonical.map(section => shownItems(section.list));
    const shifted = served.some((section, index) => (servedItems[index].length > 0 && !canonical[index].list) || (canonicalItems[index].length > 0 && !section.list));
    if (shifted)
        return served.map(withoutBlanks);
    const unseen = new Set(unseenIndexes);
    const keys = uniqueKeys(canonical.map((section, index) => (unseen.has(index) ? undefined : contentKey(section.title))));
    return served.map((section, index) => {
        const keyed = withoutBlanks(section, index);
        const items = servedItems[index];
        const reference = canonicalItems[index];
        if (!pairable(items, reference))
            return keyed;
        const key = keys[index];
        if (key)
            keyed.sectionKey = key;
        if (items.length > 0) {
            const labelKeys = uniqueKeys(reference.map(entry => contentKey(leadingLabel(entry.item))));
            const itemKeys = items.map((entry, place) => {
                const itemKey = labelKeys[place];
                return itemKey !== undefined && leadingLabel(entry.item) !== undefined ? itemKey : null;
            });
            if (itemKeys.some(itemKey => itemKey !== null))
                keyed.itemKeys = itemKeys;
        }
        return keyed;
    });
}
//# sourceMappingURL=issuer-sections.utils.js.map